import { z } from 'zod';
import { getObjectHash, sortObject } from '../helpers/object.helpers';

// ---------------------------------------------------------------------------
// Scope targets — individual types + discriminated union
// ---------------------------------------------------------------------------

export type AiOrganizationScope = { scope: 'organization' };
export type AiProjectScope = { scope: 'project' };
export type AiDesignScope = {
  scope: 'design';
  buildingIndex: number;
};
export type AiElementsScope = {
  scope: 'elements';
  versionId: string;
  elementId?: string;
  /**
   * Proposal the user has selected in the Build view. Element mutations run
   * against this proposal when set; omitted means baseline. Synced from the
   * UI per turn — the model never passes it (MCP callers pass an explicit
   * `proposalId` tool param instead).
   */
  proposalId?: string;
};

export type AiScopeTarget =
  AiOrganizationScope | AiProjectScope | AiDesignScope | AiElementsScope;

export type AiScope = AiScopeTarget['scope'];

/**
 * Tool domains — grouping used by the in-app chat registry and MCP. Each
 * registered {@link AiToolDefinition} declares a domain; a given scope target
 * composes one or more domains via `resolveToolsForContext`.
 */
export const toolDomains = [
  'design',
  'project',
  'elements',
  'organization',
] as const;
export type ToolDomain = (typeof toolDomains)[number];

/**
 * Parse/validate {@link AiScopeTarget} from JSON (e.g. query string).
 * Legacy `projectId` on project/design/elements variants is silently stripped
 * by zod's default `.strip()` so historical message rows still parse cleanly —
 * projectId now lives on the conversation, not the scope target.
 */
export const aiScopeTargetSchema = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('organization') }),
  z.object({ scope: z.literal('project') }),
  z.object({
    scope: z.literal('design'),
    buildingIndex: z.number(),
  }),
  z.object({
    scope: z.literal('elements'),
    versionId: z.string(),
    elementId: z.string().optional(),
    proposalId: z.string().optional(),
  }),
]);

// ---------------------------------------------------------------------------
// Scope type guards
// ---------------------------------------------------------------------------

export const isOrganizationScope = (
  t: AiScopeTarget,
): t is AiOrganizationScope => t.scope === 'organization';
export const isProjectScope = (t: AiScopeTarget): t is AiProjectScope =>
  t.scope === 'project';
export const isDesignScope = (t: AiScopeTarget): t is AiDesignScope =>
  t.scope === 'design';
export const isElementsScope = (t: AiScopeTarget): t is AiElementsScope =>
  t.scope === 'elements';

// ---------------------------------------------------------------------------
// Conversation model
// ---------------------------------------------------------------------------

export const aiMessageRoleSchema = z.enum(['user', 'assistant', 'tool']);
export type AiMessageRole = z.infer<typeof aiMessageRoleSchema>;

export const aiChatActionSchema = z.object({
  kind: z.literal('open-project'),
  organization: z.string(),
  projectId: z.number(),
  versionId: z.string(),
});
export type AiChatAction = z.infer<typeof aiChatActionSchema>;

export const aiToolCallSchema = z.object({
  name: z.string(),
  arguments: z.record(z.string(), z.unknown()),
  result: z
    .object({
      success: z.boolean(),
      action: aiChatActionSchema.optional(),
      error: z.string().optional(),
      /**
       * `projects.updated_at` after this turn persisted the project.
       * Absent when the turn did not save. A client that missed the
       * `project_update` socket uses it to refetch before its next save.
       */
      projectUpdatedAt: z.string().optional(),
    })
    .optional(),
});
export type AiToolCall = z.infer<typeof aiToolCallSchema>;

export const aiMessageStatusSchema = z.enum([
  'pending',
  'processing',
  'processed',
]);
export type AiMessageStatus = z.infer<typeof aiMessageStatusSchema>;

/**
 * A file attached to a chat message: a reference to an uploaded file, with the
 * name and kind copied so the thread can render without loading the file.
 */
export const aiMessageAttachmentSchema = z.object({
  /** `uploaded_files.id`. */
  fileId: z.string(),
  name: z.string(),
  kind: z.enum(['file', 'image']),
});
export type AiMessageAttachment = z.infer<typeof aiMessageAttachmentSchema>;

export const aiMessageSchema = z.object({
  id: z.string(),
  role: aiMessageRoleSchema,
  content: z.string(),
  toolCalls: z.array(aiToolCallSchema).optional(),
  status: aiMessageStatusSchema,
  responseTimeMs: z.number().nullable().optional(),
  /**
   * Scope the turn was sent from. Optional for legacy rows inserted before
   * per-message scope tracking landed — those fall back to the conversation's
   * scope (if any) at pipeline time.
   */
  scopeTarget: aiScopeTargetSchema.optional(),
  /**
   * OpenAI model requested for this message (or historically used, for
   * assistant rows backfilled from earlier deploys). Only meaningful on
   * user messages and only set when a Nodon-employee override was
   * provided; otherwise the server's default model applies.
   *
   * Stored as an unconstrained string on purpose: the set of currently
   * allowed models (see {@link OpenAiAllowedModels}) drifts over time, but
   * the DB must keep rows valid across deploys. Write-side validation
   * uses {@link isAllowedOpenAiModel} at the controller boundary; the
   * pipeline re-validates before sending to OpenAI and falls back to
   * the default for stored values that are no longer allowed.
   */
  model: z.string().optional(),
  /**
   * Files attached to this turn, as references to uploaded files. The
   * pipeline uploads them to the AI provider when needed; the client shows
   * `name`.
   */
  attachments: z.array(aiMessageAttachmentSchema).optional(),
  createdAt: z.string(),
});
export type AiMessage = z.infer<typeof aiMessageSchema>;

/**
 * Assistant messages before the first user message: ones the app wrote when
 * it made the conversation, not replies from the model.
 */
export const openingAssistantMessages = (
  messages: readonly AiMessage[],
): AiMessage[] => {
  const firstUser = messages.findIndex((message) => message.role === 'user');
  return messages
    .slice(0, firstUser === -1 ? messages.length : firstUser)
    .filter(
      (message) =>
        message.role === 'assistant' && message.content.trim() !== '',
    );
};

export const aiConversationStatusSchema = z.enum(['idle', 'processing']);
export type AiConversationStatus = z.infer<typeof aiConversationStatusSchema>;

/** Max length for persisted conversation title (user-set or first-message default). */
export const AI_CHAT_TITLE_MAX_LENGTH = 500 as const;

/**
 * Max length for a single user message. Caps payload size so accidental
 * dumps (large IFC / CSV pastes) cannot blow the model context budget.
 */
export const AI_CHAT_MESSAGE_MAX_LENGTH = 10_000 as const;

// ---------------------------------------------------------------------------
// Provider context — tagged union, one variant per AI provider
// ---------------------------------------------------------------------------

export const openAiProviderContextSchema = z.object({
  provider: z.literal('openai'),
  responseId: z.string(),
});

export const geminiProviderContextSchema = z.object({
  provider: z.literal('gemini'),
  sessionId: z.string(),
});

export const aiProviderContextSchema = z.discriminatedUnion('provider', [
  openAiProviderContextSchema,
  geminiProviderContextSchema,
]);

export type AiProviderContextOpenAi = z.infer<
  typeof openAiProviderContextSchema
>;
export type AiProviderContextGemini = z.infer<
  typeof geminiProviderContextSchema
>;
export type AiProviderContext = z.infer<typeof aiProviderContextSchema>;

/**
 * Parse a raw JSONB value into a typed {@link AiProviderContext}.
 * Returns `undefined` when the value is empty or doesn't match any known provider schema.
 */
export const parseProviderContext = (
  raw: Record<string, unknown>,
): AiProviderContext | undefined => {
  const result = aiProviderContextSchema.safeParse(raw);
  return result.success ? result.data : undefined;
};

export const aiConversationSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  /**
   * Project this conversation belongs to. `null` = organization-level chat
   * (user is not inside any project). The client store buckets conversations
   * by `(organizationId, projectId)` so each project resumes its own history.
   */
  projectId: z.number().nullable(),
  /**
   * Most-recent scope the conversation was used from. Derived at read time
   * from the latest message's {@link AiMessage.scopeTarget}, falling back
   * to the legacy per-conversation scope column for pre-restructure rows.
   * Clients use it only for metadata (history previews, socket routing);
   * the pipeline always resolves per-message scope.
   */
  scopeTarget: aiScopeTargetSchema,
  messages: z.array(aiMessageSchema),
  status: aiConversationStatusSchema,
  providerContext: aiProviderContextSchema.optional(),
  /** User-facing label; omit/null when only derived preview applies (see list COALESCE). */
  title: z.string().max(AI_CHAT_TITLE_MAX_LENGTH).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AiConversation = z.infer<typeof aiConversationSchema>;

/**
 * Conversation fields for socket updates without message bodies (smaller payload).
 * {@link preview} is the resolved display label (COALESCE(title, first user line)).
 */
export const aiConversationMetadataSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  projectId: z.number().nullable(),
  scopeTarget: aiScopeTargetSchema,
  status: aiConversationStatusSchema,
  providerContext: aiProviderContextSchema.optional(),
  title: z.string().max(AI_CHAT_TITLE_MAX_LENGTH).nullable(),
  /** Resolved label for header/history (same as list preview). */
  preview: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AiConversationMetadata = z.infer<
  typeof aiConversationMetadataSchema
>;

/**
 * Build a deterministic scope key from organization + scope target.
 * Uses {@link getObjectHash} on a key-sorted object so the same logical target
 * always produces the same hash regardless of property insertion order.
 */
export const buildScopeKey = (
  organizationId: string,
  target: AiScopeTarget,
): string => getObjectHash(sortObject({ organizationId, ...target }));

/**
 * Type guard for {@link AiConversation}.
 */
export const isAiConversation = (data: unknown): data is AiConversation =>
  aiConversationSchema.safeParse(data).success;

export const isAiConversationMetadata = (
  data: unknown,
): data is AiConversationMetadata =>
  aiConversationMetadataSchema.safeParse(data).success;

// ---------------------------------------------------------------------------
// Admin: scope + tool descriptors
// ---------------------------------------------------------------------------

/**
 * Plain-JSON view of a single tool the AI chat pipeline can call. Used by the
 * admin page that lists all registered tools — not at runtime.
 */
export interface AiToolDescriptor {
  name: string;
  description: string;
  /** Strict JSON Schema (as emitted by OpenAI's zodResponsesFunction). */
  parameters: unknown;
  domain: ToolDomain;
}

export interface AiScopeDescriptor {
  id: AiScope;
  instructions: string;
  tools: AiToolDescriptor[];
}

/** Lightweight conversation entry used for history lists. */
export type AiConversationSummary = {
  id: string;
  preview: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Admin view of a conversation in the cross-user/cross-org index. Adds the
 * fields {@link AiConversationSummary} hides (`organizationId`, `userName`,
 * `scopeTarget`, `status`, `title`) so admins can tell rows apart without
 * opening each one. There is no `userId` on this shape — only a resolved
 * display label in `userName`.
 */
export type AiAdminConversationSummary = {
  id: string;
  organizationId: string;
  /** Human-readable user label (name/nickname) resolved server-side; `null` when lookup failed. */
  userName: string | null;
  scopeTarget: AiScopeTarget;
  status: AiConversationStatus;
  title: string | null;
  preview: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Derives list row fields from a full conversation (matches server COALESCE(title, first user line)).
 * {@link AiConversationSummary.preview} may be `''`. History lists omit those rows.
 */
export const getConversationSummaryFromConversation = (
  c: AiConversation,
): AiConversationSummary => {
  const titled = c.title?.trim();
  const firstUserMessage = c.messages.find((m) => m.role === 'user');
  const preview = titled || firstUserMessage?.content?.trim() || '';
  return {
    id: c.id,
    preview,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  };
};
